'use client';
import { hideModal, showToast } from '@/redux/navigationSlice';
import { pushProspectData, pushProspectDocuments, resetDocumentData } from '@/redux/prospectDataSlice';
import { RootState } from '@/redux/store';
import { Box, Button, CircularProgress, Stack, TextField, Typography } from '@mui/material';
import { changeBackofficeFileStatus, patchBackofficeFile } from '@repo/backoffice-api';
import { useRouter } from 'next/navigation';
import { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

/**
 * Renders a confirm document change body for a Modal component.
 *
 * @return {React.JSX.Element} The rendered ConfirmDocumentChangeBody component.
 */
export const ConfirmDocumentChangeBody = (): React.JSX.Element => {
  const dispatch = useDispatch();
  const router = useRouter();
  const { prospectId, documentData, allDocuments } = useSelector((state: RootState) => state.prospectData);
  const { status, title, id: documentId, comment } = documentData;

  const [loading, setLoading] = useState(false);

  const [rejectionMessage, setRejectionMessage] = useState(comment || '');
  const [rejectionMessageError, setRejectionMessageError] = useState(false);

  const isEditingComment = comment && status === 'REJECTED';
  const areAllOtherDocumentsApproved = useMemo(() => {
    if (!allDocuments.length || !documentId) return false;
    return allDocuments.filter((doc) => doc.id !== documentId).every((doc) => doc.status === 'APPROVED');
  }, [allDocuments, documentId]);

  const changeDocumentStatus = async (): Promise<void> => {
    if (!prospectId || !documentId || !status) {
      console.error('Invalid data:', prospectId, documentId, status);
      dispatch(showToast({ text: 'Los datos del documento a modificar no son válidos', kind: 'error' }));
      dispatch(resetDocumentData());
      dispatch(hideModal());
      return;
    }

    setLoading(true);
    let notAuth = false;
    try {
      let data;
      if (isEditingComment) {
        data = await patchBackofficeFile({
          prospectId,
          documentId,
          comment: rejectionMessage,
        });
      } else {
        data = await changeBackofficeFileStatus({
          prospectId,
          documentId,
          status: status === 'APPROVED' ? 'approve' : 'reject',
          comment: rejectionMessage,
        });
      }
      if (data?.notAuth) {
        notAuth = true;
        dispatch(showToast({ text: 'Usuario no autenticado', kind: 'error' }));
        throw new Error('User not authenticated');
      }
      if (data?.softError || data?.hardError) {
        dispatch(
          showToast({ text: data?.message || 'Hubo un error al modificar el estado del documento', kind: 'error' }),
        );
        throw new Error(data?.message || 'Error changing document status');
      } else {
        dispatch(
          showToast({
            text: `Documento ${status === 'APPROVED' ? 'aprobado' : 'rechazado'} exitosamente`,
            kind: 'success',
          }),
        );

        // Update local Redux state with the returned prospect data so the UI
        // updates immediately without waiting for a full page reload.
        if (data) {
          try {
            dispatch(pushProspectDocuments(data.documentationView || []));
            dispatch(
              pushProspectData({
                prospectId: data.id,
                legalName: data.legalName,
                state: data.state,
                substate: data.substate,
                availableSubstates: data.availableSubstates,
              }),
            );
          } catch (err) {
            console.warn('Could not update prospect data in store:', err);
          }
        }

        // Revalidate the current page without forcing a route change.
        router.refresh();
      }
    } catch (error: any) {
      console.error(error);
    } finally {
      if (notAuth) {
        router.push('/login');
        router.refresh();
      }
      setLoading(false);
      dispatch(resetDocumentData());
      dispatch(hideModal());
    }
  };

  const lastApprovalWarning = () => (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'error.main',
        bgcolor: 'rgba(211,47,47,0.06)',
        color: 'error.main',
        p: 2,
        borderRadius: 1,
        display: 'flex',
        gap: 1,
        alignItems: 'center',
        mt: 2,
      }}
    >
      <Typography variant="body2">
        ¡Atención! Todos los demás documentos ya fueron aprobados. De aprobar también este documento, el prospecto
        cambiará de estado.
      </Typography>
    </Box>
  );

  return (
    <>
      <Typography variant="h2">{title}</Typography>
      <TextField
        label="Mensaje de revisión"
        required
        value={rejectionMessage}
        error={rejectionMessageError}
        helperText={rejectionMessageError ? 'Ingresa un mensaje válido' : ''}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) => setRejectionMessage(event.target.value)}
        onBlur={() => setRejectionMessageError(!rejectionMessage)}
        onClick={() => setRejectionMessageError(false)}
        multiline
        rows={4}
        sx={{
          display: status === 'REJECTED' ? 'flex' : 'none',
          textAlign: 'start',
        }}
      />
      {areAllOtherDocumentsApproved && lastApprovalWarning()}
      <Stack direction="row" gap={2} width="100%" mt={2}>
        <Button fullWidth variant="outlined" onClick={() => dispatch(hideModal())}>
          Cancelar
        </Button>
        <Button
          fullWidth
          disabled={loading || (status === 'REJECTED' && !rejectionMessage)}
          onClick={(e) => {
            e.preventDefault();
            changeDocumentStatus();
          }}
        >
          {loading ? (
            <Box sx={{ width: '100%', alignItems: 'center' }}>
              <CircularProgress color="inherit" size={20} />
            </Box>
          ) : (
            <span>Confirmar</span>
          )}
        </Button>
      </Stack>
    </>
  );
};
